// ─────────────────────────────────────────────────────────────────────────────
// ConsentProvider.jsx
//
// Centrálny správca stavu súhlasu pre celú aplikáciu.
// Renderuje CookieBanner a CookieSettings interne – žiadna ďalšia konfigurácia
// v strome komponentov nie je potrebná.
// ─────────────────────────────────────────────────────────────────────────────
import { createContext, useState, useCallback, useEffect } from "react";
import { loadConsent, saveConsent, defaultConsent } from "./consent.storage";
import CookieBanner from "../components/CookieBanner/CookieBanner";
import CookieSettings from "../components/CookieSettings/CookieSettings";

/** @type {React.Context<ConsentContextValue | null>} */
export const ConsentContext = createContext(null);

/**
 * ConsentProvider – obalí celú appku.
 *
 * @param {{ children: React.ReactNode }} props
 */
export function ConsentProvider({ children }) {
  // null = načítavanie, inak ConsentState objekt
  const [consent, setConsent] = useState(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Načítame uložený súhlas pri prvom mountovaní
  useEffect(() => {
    const saved = loadConsent();
    if (saved) {
      // Existuje platný súhlas – nepotrebujeme banner
      setConsent(saved);
      setBannerVisible(false);
    } else {
      // Prvá návšteva alebo zastaraná verzia politiky – zobrazíme banner
      setConsent(defaultConsent);
      setBannerVisible(true);
    }
    setInitialized(true);
  }, []);

  /** Prijať všetky kategórie */
  const acceptAll = useCallback(() => {
    const saved = saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    setConsent(saved);
    setBannerVisible(false);
    setSettingsOpen(false);
  }, []);

  /** Odmietnuť všetky voliteľné kategórie */
  const rejectAll = useCallback(() => {
    const saved = saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
    setConsent(saved);
    setBannerVisible(false);
    setSettingsOpen(false);
  }, []);

  /**
   * Uložiť vlastné nastavenia kategórií
   * @param {{ analytics: boolean, marketing: boolean, preferences: boolean }} choices
   */
  const saveCustom = useCallback((choices) => {
    const saved = saveConsent({
      necessary: true,
      analytics: choices.analytics ?? false,
      marketing: choices.marketing ?? false,
      preferences: choices.preferences ?? false,
    });
    setConsent(saved);
    setBannerVisible(false);
    setSettingsOpen(false);
  }, []);

  /** Otvoriť nastavenia (z footeru, profilstránky, atď.) */
  const openSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  /** Znova otvoriť banner (napr. pri testovaní) */
  const reopenBanner = useCallback(() => {
    setBannerVisible(true);
  }, []);

  // Nič nerenderujeme kým neprečítame localStorage (predídeme flash)
  if (!initialized) return null;

  return (
    <ConsentContext.Provider
      value={{
        consent,
        acceptAll,
        rejectAll,
        saveCustom,
        openSettings,
        reopenBanner,
      }}
    >
      {children}

      {/* Banner sa zobrazí iba ak neexistuje platný súhlas */}
      {bannerVisible && (
        <CookieBanner
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onCustomize={() => {
            setBannerVisible(false);
            setSettingsOpen(true);
          }}
        />
      )}

      {/* Nastavenia – otvárateľné kedykoľvek cez useConsent().openSettings() */}
      {settingsOpen && (
        <CookieSettings
          consent={consent}
          onSave={saveCustom}
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </ConsentContext.Provider>
  );
}
